import React from 'react';
import { Card, CardBody } from '@material-tailwind/react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import LoadingPanel from './LoadingPanel';

const MonthlyInvoiceOwnersReport = ({
  ownerLoading,
  ownerChart,
  ownerRows,
  ownerColors,
  formatMoney
}) => {
  if (ownerLoading) {
    return <LoadingPanel label="Loading owner totals..." />;
  }

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card className="shadow-lg">
        <CardBody className="p-0">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Top owners by net</div>
          </div>
          <div className="h-[360px] px-3 py-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ownerChart} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={90} />
                <Tooltip />
                <Bar dataKey="net" radius={[0, 10, 10, 0]}>
                  {ownerChart.map((entry, index) => (
                    <Cell key={entry.name} fill={ownerColors[index % ownerColors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardBody>
      </Card>

      <Card className="shadow-lg">
        <CardBody className="p-0">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Owner summary table</div>
          </div>
          <div className="max-h-[36rem] overflow-auto">
            <table className="min-w-[1100px] w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Owner</th>
                  <th className="px-4 py-3 text-right font-semibold">Count</th>
                  <th className="px-4 py-3 text-right font-semibold">Paid</th>
                  <th className="px-4 py-3 text-right font-semibold">Sent</th>
                  <th className="px-4 py-3 text-right font-semibold">Draft</th>
                  <th className="px-4 py-3 text-right font-semibold">Reversed</th>
                  <th className="px-4 py-3 text-right font-semibold">Gross</th>
                  <th className="px-4 py-3 text-right font-semibold">Discount</th>
                  <th className="px-4 py-3 text-right font-semibold">Net</th>
                </tr>
              </thead>
              <tbody>
                {ownerRows.map((row) => (
                  <tr key={row.owner_code} className="border-t border-slate-200">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{row.owner_code}</div>
                      <div className="text-xs text-slate-500">{row.owner_name}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{Number(row.count || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{Number(row.paid_count || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{Number(row.sent_count || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{Number(row.draft_count || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{Number(row.reversed_count || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatMoney(row.gross_amount || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatMoney(row.discount_amount || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatMoney(row.net_amount || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default MonthlyInvoiceOwnersReport;
